import { useMemo, useState } from 'react'
import { selectCurrentClientId, selectUpdateClientPackageId, selectUpdatePackageId, setClearTypeOfShowClient } from '../../../features/client/clientSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useDynamicQueryData } from '../../../hooks/api/useDynamicQueryData'
import ErrorMessage from '../../ui/ErrorMessage'
import EmptyData from '../../ui/EmptyData'
import Loading from '../../ui/Loading'
import ClientUpdatePackageProductsHeader from '../../common/headers/ClientUpdatePackageProductsHeader'
import ClientUpdatePackageProductsTHeads from '../../common/theads/ClientUpdatePackageProductsTHeads'
import Button from '../../ui/Button'
import { useMutationData } from '../../../hooks/api/useMutationData'
import { UpdateSpecialProductBody } from '../../../features/client/clientRequestBody'
import { showToast } from '../../../toastify/Toast'
import { handleApiError } from '../../../utils/handleApiError'
import ProductAvailable from '../../client/ProductAvailable'
import { ProductSummary } from '../../../features/package/packageInterface'
import { useClientPopulateWrap } from '../../../hooks/populate/useClientPopulateWrap'
import ClientUpdatePackageProductsTBody from '../../common/tbody/ClientUpdatePackageProductsTBody'

export default function ClientUpdatePackageProducts() {

    const dispatch = useDispatch()
    const clientId = useSelector(selectCurrentClientId);
    const openPackageId = useSelector(selectUpdateClientPackageId);
    const specialPackageId = useSelector(selectUpdatePackageId);
    const { packageSummaryBaseOnIdQuery, isErrorPackageSummaryBaseOnIdQuery, isLoadingPackageSummaryBaseOnIdQuery, errorMessagePackageSummaryBaseOnIdQuery, refetchPackageSummaryBaseOnIdQuery } = useDynamicQueryData({clientId})
    const [isEditing, setIsEditing] = useState(false);
    const [editableProducts, setEditableProducts] = useState<ProductSummary[]>([]);
    const [showAddProductSidebar, setShowAddProductSidebar] = useState(false);
    const { updateClientSpecialPackage, isLoadingUpdateClientSpecialPackage, } = useMutationData()
    const { repopulateAll } = useClientPopulateWrap()

    const selectedPackage = useMemo(() => {
        return packageSummaryBaseOnIdQuery?.find(
            pkg => pkg.clientPackageId === openPackageId
        );
    }, [packageSummaryBaseOnIdQuery, openPackageId, isEditing]);
    
    const productsToDisplay = useMemo(() => {
        if (!isEditing) {
            return selectedPackage?.products ?? [];
        }

        return [...editableProducts].sort((a, b) => {
            if (a.type === "new" && b.type !== "new") return -1;
            if (a.type !== "new" && b.type === "new") return 1;
            return 0;
        });
    }, [isEditing, editableProducts, selectedPackage]);

    const handleUpdate = () => {
        if (!selectedPackage?.products) return;

        setEditableProducts(
            selectedPackage.products.map((product) => ({
                ...product,
                // isAvailable: product.isAvailable,
                isActive: product.isActive,
                type: 'old',
                addons: (product.addons ?? []).map((addon) => ({
                    ...addon,
                    isActive: addon.isActive,
                })),
            }))
        );

        setIsEditing(true);
    };

    const handleToggleProduct = (productID: number) => {
        setEditableProducts((prev) =>
            prev.map((product) =>
                product.productID === productID
                    ? { ...product, isActive: !product.isActive }
                    : product
            )
        );
    };

    const handleRemoveNewProduct = (productID: number) => {
        setEditableProducts(prev =>
            prev.filter(
                product =>
                    !(product.productID === productID && product.type === "new")
            )
        );
    };

    const handleSave = async () => {
        if(!specialPackageId || !clientId) return
        if (editableProducts.length === 0) {
            showToast("No products to update.", "warning");
            return;
        }
        try {
            const productUpdate = editableProducts.map(product => ({
                productID: product.productID,
                isActive: product.isActive,
            }));
            const requestBody: UpdateSpecialProductBody = {
                specialPackageID: specialPackageId,
                items: productUpdate
            }

            const response = await updateClientSpecialPackage({ clientId, body: requestBody}).unwrap()
            await refetchPackageSummaryBaseOnIdQuery();
            repopulateAll()
            showToast(response.message, 'success')
            setEditableProducts([])
            setIsEditing(false);
        } catch (error) {
            handleApiError(error)
        }
    };

    const handleCancel = () => {
        setEditableProducts([])
        setIsEditing(false);
    };

    const handleCloseModal = () => {
        dispatch(setClearTypeOfShowClient())
    }

    return (
        <div className={`fixed inset-0 m-0 p-0 bg-black bg-opacity-30 backdrop-blur-sm flex justify-center items-center z-[102] overflow-hidden`}>
            <div className={`px-5 bg-white relative w-[90vw] mx-5  rounded-lg shadow flex md:w-[75vw] lg:max-w-[60vw] lg:mx-auto overflow-hidden`}>
                <div className="flex flex-col w-full min-h-[80dvh] max-h-[80dvh] overflow-hidden">
                    <div className="flex flex-col py-5 space-y-3 h-full relative">
                        <ClientUpdatePackageProductsHeader onClose={handleCloseModal}/>
                            <div className='flex-1 overflow-hidden bg-white rounded border'>
                                <div className='overflow-y-auto h-full'>
                                    <table className='min-w-max w-full'>
                                        <ClientUpdatePackageProductsTHeads/>
                                        <ClientUpdatePackageProductsTBody
                                            prod={productsToDisplay}
                                            isLoading={isLoadingPackageSummaryBaseOnIdQuery}
                                            isError={isErrorPackageSummaryBaseOnIdQuery}
                                            errorMessage={errorMessagePackageSummaryBaseOnIdQuery}
                                            isEmpty={productsToDisplay?.length === 0}
                                            isEditing={isEditing}
                                            handleRemoveNewProduct={handleRemoveNewProduct}
                                            handleToggleProduct={handleToggleProduct}
                                        />
                                    </table>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                {isEditing ? (
                                    <>
                                        <Button
                                            onClick={handleSave}
                                            className={`px-8 py-1 text-xs rounded bg-cbColor text-white`}
                                            btnName={`${isLoadingUpdateClientSpecialPackage ? 'Saving...' : 'Save'}`}
                                        />
                                        <Button
                                            onClick={() => setShowAddProductSidebar(true)}
                                            disable={isLoadingUpdateClientSpecialPackage}
                                            className={`px-8 py-1 bg-blue-600 text-xs text-white rounded border`}
                                            btnName='Add product'
                                        />
                                        <Button
                                            onClick={handleCancel}
                                            disable={isLoadingUpdateClientSpecialPackage}
                                            className={`px-8 py-1 bg-gray-200 text-xs  rounded border`}
                                            btnName='Cancel'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            onClick={handleUpdate}
                                            className={`px-8 py-1 text-xs rounded bg-blue-600 text-white`}
                                            btnName='Update'
                                        />
                                    </>
                                )}
                            </div>
                        {/* )} */}
                    </div>
                </div>
                <ProductAvailable
                    addProducts={setEditableProducts}
                    products={editableProducts}
                    showAddProductSidebar={showAddProductSidebar}
                    setShowAddProductSidebar={setShowAddProductSidebar}
                />
            </div>
            {showAddProductSidebar && (
                <div className="absolute inset-0 bg-black/30 z-10" />
            )}
        </div>
    )
}
